import { ISelectOpt } from 'app/components/common/Select';
import { AgenturModel } from 'app/models/agentur.model';
import { Steart, SteartModel } from 'app/models/core/steart.model';
import { ApModel } from 'app/models/documents/ap.model';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import moment from 'moment';
import { AgenturStore } from '../agentur.store';
import { UserStore } from '../user.store';
import { AgtBpVM, AgtBranchenplanungUiStore } from './agt.branchenplanung.ui.store';
import { AgtRingPos } from './agt.ring.ui.store';
import { AgtSchwerpunktVM, AgtSchwerpunktUiStore } from './agt.schwerpunkt.ui.store';
import { AgtSegmentUiStore, AgtSegmentVM } from './agt.segment.ui.store';
import { DocumentUiStore, DocumentVM } from './document.ui.store';
import { SteartStore } from '../steart.store';
import { AgenturJahresthemenStore, AgtJahresthemenModel } from '../agentur.jahresthemen.store';
import { AgenturFilterUiStore, IAgtFilterable, IFilterOpts, defaultOpts } from './agt.list.filter.ui.store';
import { BaseUiStore } from './base.ui.store';
import Config from 'Config';
import { AgtFocusthemenModel, AgtFocusthemenStore } from '../agt.focusthemen.store';


export class AgenturListUiStore extends BaseUiStore<AgenturModel> {
	name = 'AgenturListUiStore';
	agenturStore: AgenturStore;
	agtBranchenplanungUiStore: AgtBranchenplanungUiStore;
	agtSchwerpunktUiStore: AgtSchwerpunktUiStore;
	documentUiStore: DocumentUiStore;
	agtSegmentUiStore: AgtSegmentUiStore;
	userStore: UserStore;
	steartStore: SteartStore;
	agenturJahresthemenStore: AgenturJahresthemenStore;
	agtFocusthemenStore: AgtFocusthemenStore;

	constructor(agenturStore: AgenturStore, userStore: UserStore, agtFocusthemenStore: AgtFocusthemenStore) {
		super();
		makeObservable(this);
		this.agenturStore = agenturStore;
		this.userStore = userStore;
		this.agtBranchenplanungUiStore = {} as any;
		this.agtSchwerpunktUiStore = {} as any;
		this.documentUiStore = {} as any;
		this.agtSegmentUiStore = {} as any;
		this.steartStore = {} as any;
		this.agenturJahresthemenStore = {} as any;
		this.agtFocusthemenStore = agtFocusthemenStore;
	}
	setDependenStores(
		agtBranchenplanungUiStore: AgtBranchenplanungUiStore,
		agtSchwerpunktUiStore: AgtSchwerpunktUiStore,
		documentUiStore: DocumentUiStore,
		agtSegmentUiStore: AgtSegmentUiStore,
		steartStore: SteartStore,
		agenturJahresthemenStore: AgenturJahresthemenStore,
		agenturFilterUiStore: AgenturFilterUiStore,
	) {
		this.agtBranchenplanungUiStore = agtBranchenplanungUiStore;
		this.agtSchwerpunktUiStore = agtSchwerpunktUiStore;
		this.documentUiStore = documentUiStore;
		this.agtSegmentUiStore = agtSegmentUiStore;
		this.steartStore = steartStore;
		this.agenturJahresthemenStore = agenturJahresthemenStore;
		this.currentFilter = agenturFilterUiStore;
	}

	currentFilter: AgenturFilterUiStore = new AgenturFilterUiStore({} as any, {} as any, {} as any);

	@action
	applyFilter(items: AgenturModel[], opts?: IFilterOpts): AgenturModel[] {
		this.totalItemCount = this.items.length;
		const agtIds = this.items.filter((i) => this.currentFilter.applyFilter(i, opts || defaultOpts)).map((i) => i.agtId);
		this.totalFilterdCount = agtIds ? agtIds.length : 0;
		return items.filter((i) => agtIds.includes(i.agtId));
	}

	@observable
	filteredItems: AgenturModel[] = [];

	@observable
	totalItemCount: number = 0;

	@observable
	totalFilterdCount: number = 0;

	// for views that filter further after applyFilter/applyFilterOnAgt, so the
	// "X / Y Agenturen" badge matches the actually visible rows
	@action
	updateCounts(filteredCount: number, totalCount?: number) {
		this.totalFilterdCount = filteredCount;
		if (totalCount !== undefined) {
			this.totalItemCount = totalCount;
		}
	}

	@computed
	get filteredAgtIds(): number[] {
		return this.applyFilter(this.items).map((i) => i.agtId);
	}

	@computed
	get filteredAgtIdsAsFilter(): string[] | undefined {
		if (this.filteredAgtIds.length > 0) {
			return [`vtnr_id IN (${this.filteredAgtIds.join(', ')})`];
		}
	}

	@action
	applyFilterOnAgt(items: IAgtFilterable[], opts?: IFilterOpts) {
		this.totalItemCount = items.length;
		const agtIds = this.items.filter((i) => this.currentFilter.applyFilter(i, opts || defaultOpts)).map((i) => i.agtId);
		const res = items.filter((i) => agtIds.includes(i.agt.agtId));
		this.totalFilterdCount = res ? res.length : 0;
		return res;
	}
	@action
	applyFilterOnGpRelevantAgt(items: IAgtFilterable[], opts?: IFilterOpts) {
		return items.filter(item => !(item.agt.gpShort === 'AGP' || !item.agt.vdId || !item.agt.gsId || item.agt.gsId === 'XX'))
	}

	applyFilterOnRing(items: AgtRingPos[], opts?: IFilterOpts) {
		const agtIds = this.items.filter((i) => this.currentFilter.applyFilter(i, opts || defaultOpts)).map((i) => i.agtId);
		return items.filter((i) => agtIds.includes(i.agt.agtId));
	}

	@computed
	get filterHasChanged() {
		return this.filtered.length + this.currentFilter.changedString;
	}

	@computed
	get filtered() {
		return this.applyFilter(this.items);
	}

	@computed
	get inspIdOptions() {
		const opts: ISelectOpt[] = [];

		this.items.forEach((agt) => {
			const x = opts.find((o) => o.value === agt.gebiet.inspId.toString());
			if (!x) {
				opts.push({
					label: agt.gebiet.inspId.toString(),
					value: agt.gebiet.inspId.toString(),
				});
			}
		});
		return opts;
	}

	@computed
	get steartMultiOpts() {
		return [
			{
				label: 'LDP',
				value: '1001',
			},
			{
				label: 'EDT',
				value: '1003',
			},
			{
				label: 'EDP',
				value: '1002',
			},
		];
	}

	@action
	async _baseLoad() {
		const isVBL = this.agenturStore.session.isVbl;

		await Promise.all([
			this.userStore.findAll(),
			this.agenturStore.findAll(),
			this.agtBranchenplanungUiStore.load(),
			this.agtSchwerpunktUiStore.load(),
			this.documentUiStore.load(),
			isVBL ? this.agtSegmentUiStore.load() : '',
			this.steartStore.findAll(),
			this.agenturJahresthemenStore.findAll(),
			this.currentFilter.kvBnrAgtFilterStore.findAll(),
		]);

		const year = Config.currentPlanYear;
		// const year = 2025;
		const agts = this.agenturStore.agenturen;
		const users = this.userStore._items;
		let bps = this.agtBranchenplanungUiStore.items.filter((s) => s.year === year).slice();
		let swps = this.agtSchwerpunktUiStore.items.filter((s) => s.year === year).slice();

		const docs = this.documentUiStore.items.filter((s) => s.planYear === year);
		let segs = this.agtSegmentUiStore.items.slice();
		let themen = this.agenturJahresthemenStore._items.slice();
		const res: AgenturModel[] = [];
		let noEdpCount = 0;

		swps = swps.sort((a, b) => a.agtId - b.agtId);
		segs = segs.sort((a, b) => a.agtId - b.agtId);
		agts.forEach((agt) => {
			const agtId = agt.agtId;
			const agtUsers = users.filter((u) => agt.bnrIds && agt.bnrIds.includes(u.bnrId) && !u.isGSL);
			if (agtUsers.length > 0) {
				agt.users = agtUsers;
			}
			let bp: AgtBpVM;
			[bp, bps] = this.findAndSplice(bps, (b) => b.agtId === agtId);

			let sw: AgtSchwerpunktVM;
			[sw, swps] = this.findAndSplice(swps, (b) => b.agtId === agtId && b.year === year);
			const dcs = docs.filter((b) => b.agtId === agtId);

			let xsegs: AgtSegmentVM[];
			let seg;
			[seg, segs] = this.findAndSplice(segs, (b) => b.agtId === agtId && b.year === year);
			let steart;
			if (agt._steartStringId) {
				steart = this.steartStore.findSteart(parseInt(agt._steartStringId));
			}

			let agtThemen: AgtJahresthemenModel;
			[agtThemen, themen] = this.findAndSplice(themen, (x) => x.agtId === agtId);

			// Direct property assignment instead of expensive constructor inheritance
			agt.docs = dcs;
			agt.bp = bp;
			agt.schwerpunkt = sw;
			agt.seg = seg;
			agt.steart = steart;
			agt.jahresthemen = agtThemen;
			//agt.focusThemen = []; // to be filled later
			const a = agt; // No object creation!

			if (this.agenturStore.session.isMulti) {
				// Multis sehen nur agenturen mit EDPs!
				if (!a.hasEDPbetreuungFuerMultis) {
					noEdpCount++;
					return;
				}
			}
			res.push(a);
		});
		console.log('we have ', res.length, ' agt VMs');
		if (noEdpCount) {
			console.log('ohne edp betreuung', noEdpCount);
		}
		runInAction(() => {
			this.items = res;
		});
	}

	@action
	refreshFocusthemenAndOtherStuff = async () => {

		const year = Config.currentPlanYear;
		await this.load();

		if (!this.agenturStore.session.isAdmin) {
			const docs = this.documentUiStore.items.filter((s) => s.planYear === year);
			runInAction(() => {
				this.items.forEach((agt) => {
					const dcs = docs.filter((b) => b.agtId === agt.agtId);
					agt.docs = dcs;
				});
			});
		} else {
			const focusThemen = await this.agtFocusthemenStore.getByYear(year);
			const agtFocusMap: Map<number, AgtFocusthemenModel[]> = new Map();
			focusThemen.forEach((ft) => {
				agtFocusMap.set(ft.vtnrId, [...(agtFocusMap.get(ft.vtnrId) || []), ft]);
			});
			runInAction(() => {
				this.items.forEach((agt) => {
					agt.ft = agtFocusMap.get(agt.agtId) || [];
				});
			});
		}
	}

	@computed
	get itemsForErrungenschaften() {
		return this.items.filter((i) => i.supportsDigiFaktor);
	}

	onLogout() {
		this.agenturStore.onLogout();
	}
}
